import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  BulkBodySystemGenerationJob,
  cancelBulkBodySystemGeneration,
  getActiveBulkBodySystemGeneration,
  getBulkBodySystemGenerationJob,
  startBulkBodySystemGeneration,
} from '../../../api/healthSummary';
import { ListBodySystemResponseElement } from '../../../types/apiGeneratedTypes';
import Button from '../../BaseComponents/Button';
import Loader from '../../BaseComponents/Loader';
import Modal from '../../BaseComponents/Modal';

type Props = {
  isOpen: boolean;
  bodySystem: ListBodySystemResponseElement;
  onClose: () => void;
};

const POLL_INTERVAL_MS = 2000;
const FAILED_NAME_LIMIT = 8;

const formatDuration = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainder}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${remainder}s`;
  }
  return `${remainder}s`;
};

const GenerateBodySystemForMembersModal: React.FC<Props> = ({
  isOpen,
  bodySystem,
  onClose,
}) => {
  const [isLoadingJob, setIsLoadingJob] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [job, setJob] = useState<BulkBodySystemGenerationJob | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const toastedJobIdRef = useRef<string | null>(null);

  const promptFields = useMemo(
    () => new Set((bodySystem.prompts ?? []).map((prompt) => prompt.field)),
    [bodySystem.prompts]
  );
  const hasSummaryPrompt = promptFields.has('summary');
  const hasScorePrompt = promptFields.has('score');
  const hasAnyPrompt = hasSummaryPrompt || hasScorePrompt;
  const isRunning = job?.status === 'running' || job?.status === 'queued';

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsLoadingJob(true);
    setLoadError(null);
    setJob(null);
    setIsStopping(false);
    toastedJobIdRef.current = null;

    getActiveBulkBodySystemGeneration(bodySystem.id)
      .then((activeJob) => {
        if (!cancelled) setJob(activeJob);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'Failed to load generation progress';
        setLoadError(message);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingJob(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, bodySystem.id]);

  useEffect(() => {
    if (!isRunning) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isRunning]);

  useEffect(() => {
    if (!job || (job.status !== 'running' && job.status !== 'queued')) return;
    let cancelled = false;
    const jobId = job.job_id;

    const poll = async () => {
      try {
        const next = await getBulkBodySystemGenerationJob(jobId);
        if (cancelled) return;
        setJob(next);
        if (next.status !== 'running' && next.status !== 'queued' && toastedJobIdRef.current !== next.job_id) {
          toastedJobIdRef.current = next.job_id;
          if (next.status === 'completed' && next.failed === 0) {
            toast.success(
              `Generated score and summary for ${next.generated} ${
                next.generated === 1 ? 'member' : 'members'
              }`
            );
          } else if (next.status === 'cancelled') {
            toast.warning(`${next.generated} generated, ${next.failed} failed. Stopped the remaining members.`);
          } else if (next.generated > 0) {
            toast.warning(`${next.generated} generated, ${next.failed} failed.`);
          } else {
            toast.error(next.error || 'No scores or summaries could be generated');
          }
        }
      } catch (error: unknown) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'Failed to load generation progress';
        setLoadError(message);
      }
    };

    void poll();
    const timer = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [job?.job_id, job?.status]);

  const handleClose = () => {
    if (isRunning) {
      void handleStop();
      return;
    }
    onClose();
  };

  const handleStop = async () => {
    if (!job || (job.status !== 'running' && job.status !== 'queued') || isStopping) return;
    setIsStopping(true);
    try {
      const next = await cancelBulkBodySystemGeneration(job.job_id);
      setJob(next);
      toast.info('Stopping after the current member finishes');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to stop generation';
      toast.error(message);
      setIsStopping(false);
    }
  };

  const handleGenerate = async () => {
    if (isStarting || isRunning || isLoadingJob || !hasAnyPrompt) return;
    setIsStarting(true);
    setLoadError(null);
    try {
      const started = await startBulkBodySystemGeneration(bodySystem.id);
      setJob(started);
      if (started.status !== 'running' && started.total === 0) {
        toast.warning('No members found to generate');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to start generation';
      setLoadError(message);
      toast.error(message);
    } finally {
      setIsStarting(false);
    }
  };

  const finishedCount = (job?.generated ?? 0) + (job?.failed ?? 0);
  const startedAtMs = job?.started_at ? Date.parse(job.started_at) : now;
  const finishedAtMs = job?.finished_at ? Date.parse(job.finished_at) : now;
  const elapsedSeconds = job ? (isRunning ? now - startedAtMs : finishedAtMs - startedAtMs) / 1000 : 0;
  const averageSeconds = finishedCount > 0 ? elapsedSeconds / finishedCount : 0;
  const remainingSeconds = job && averageSeconds > 0 ? averageSeconds * (job.total - finishedCount) : 0;
  const progressPercent = job && job.total > 0 ? Math.min(100, (finishedCount / job.total) * 100) : 0;
  const isFinished = Boolean(job && !isRunning);

  const promptLabel = hasSummaryPrompt && hasScorePrompt
    ? 'the latest score and summary prompts'
    : hasSummaryPrompt
      ? 'the latest summary prompt. This body system has no score prompt, so only the summary will be generated'
      : hasScorePrompt
        ? 'the latest score prompt. This body system has no summary prompt, so only the score will be generated'
        : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Generate scores and summary"
      width="560px"
      closeOnEsc={!isRunning}
    >
      <div className="generate-all-members">
        <p className="generate-all-members__intro">
          This generates the score and summary for <strong>{bodySystem.title}</strong> for every
          member{promptLabel ? `, using ${promptLabel}` : ''}. Existing scores and summaries for
          this body system are replaced. Stop remaining, or closing this window, finishes the
          current member and leaves the rest unchanged.
        </p>

        {isLoadingJob ? (
          <div className="generate-all-members__loading">
            <Loader />
            <span>Checking for a running job</span>
          </div>
        ) : loadError ? (
          <p className="generate-all-members__error">{loadError}</p>
        ) : !hasAnyPrompt ? (
          <p className="generate-all-members__error">
            Add a summary prompt or a score prompt for this body system before generating.
          </p>
        ) : job ? (
          <p className="generate-all-members__count">
            {job.total} {job.total === 1 ? 'member' : 'members'}
          </p>
        ) : (
          <p className="generate-all-members__count">Ready to generate for every member.</p>
        )}

        {job && isRunning ? (
          <div className="generate-all-members__progress">
            <div className="generate-all-members__progress-head">
              <strong>
                {finishedCount}/{job.total} finished
              </strong>
              <span>{formatDuration(elapsedSeconds)} elapsed</span>
            </div>
            <div className="generate-all-members__bar" aria-hidden="true">
              <div style={{ width: `${progressPercent}%` }} />
            </div>
            <span>
              {job.status === 'queued'
              ? 'Starting generation'
              : job.current_member_name
                ? `Generating ${job.current}/${job.total}: ${job.current_member_name}`
                : 'Starting generation'}
            </span>
            <span>
              {job.generated} generated, {job.failed} failed
              {finishedCount > 0
                ? `. About ${formatDuration(remainingSeconds)} remaining`
                : '. Estimating remaining time'}
            </span>
          </div>
        ) : null}

        {job && isFinished ? (
          <div className="generate-all-members__result">
            <strong>
              {job.status === 'cancelled' ? 'Stopped' : job.status === 'failed' ? 'Failed' : 'Finished'} in{' '}
              {formatDuration(elapsedSeconds)}
            </strong>
            <span>
              {job.generated} generated, {job.failed} failed
              {job.status === 'cancelled' ? ` of ${job.total}` : ''}
            </span>
            {job.error ? <span>{job.error}</span> : null}
            {job.failed_names.length > 0 ? (
              <span>
                Failed: {job.failed_names.slice(0, FAILED_NAME_LIMIT).join(', ')}
                {job.failed > FAILED_NAME_LIMIT ? ` and ${job.failed - FAILED_NAME_LIMIT} more` : ''}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="generate-all-members__actions">
          <Button
            text={isRunning ? 'Stop remaining' : isFinished ? 'Close' : 'Cancel'}
            onClick={handleClose}
            backgroundColor="#ffffff"
            color="#2D334E"
            isLoading={isStopping}
          />
          {!isFinished ? (
            <Button
              text={
                isRunning
                  ? `${finishedCount}/${job?.total ?? 0} finished`
                  : 'Generate'
              }
              onClick={() => void handleGenerate()}
              isLoading={isStarting || isRunning}
              isDisabled={isStarting || isRunning || isLoadingJob || Boolean(loadError) || !hasAnyPrompt}
            />
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default GenerateBodySystemForMembersModal;
